export const MIN_GRID_ZOOM = 1;
export const MAX_GRID_ZOOM = 5;

export const DEFAULT_GRID_VIEW_STATE = Object.freeze({
  zoom: 1,
  centerX: 0.5,
  centerY: 0.5,
});

const STORAGE_PREFIX = 'orchestrator_grid_view_v1';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const fitGridContentToViewport = (width, height, aspectRatio, fitMode = 'contain') => {
  const viewportWidth = Number(width);
  const viewportHeight = Number(height);
  const ratio = Number(aspectRatio);
  if (!viewportWidth || !viewportHeight || !Number.isFinite(ratio) || ratio <= 0) {
    return { left: 0, top: 0, width: viewportWidth || 0, height: viewportHeight || 0 };
  }

  const size = fitMode === 'cover' ? Math.max : Math.min;
  const contentWidth = size(viewportWidth, viewportHeight * ratio);
  const contentHeight = contentWidth / ratio;
  return {
    left: (viewportWidth - contentWidth) / 2,
    top: (viewportHeight - contentHeight) / 2,
    width: contentWidth,
    height: contentHeight,
  };
};

export const normalizeGridViewState = (view = {}, viewport = null) => {
  const requestedZoom = Number(view.zoom);
  const zoom = clamp(
    Number.isFinite(requestedZoom) ? requestedZoom : MIN_GRID_ZOOM,
    MIN_GRID_ZOOM,
    MAX_GRID_ZOOM
  );
  let edgeX = 0;
  let edgeY = 0;
  if (viewport?.width && viewport?.height) {
    const content = fitGridContentToViewport(
      viewport.width,
      viewport.height,
      viewport.aspectRatio,
      viewport.fitMode
    );
    if (content.width && content.height) {
      edgeX = Math.min(0.5, viewport.width / (2 * content.width * zoom));
      edgeY = Math.min(0.5, viewport.height / (2 * content.height * zoom));
    }
  }
  const requestedX = Number(view.centerX);
  const requestedY = Number(view.centerY);

  return {
    zoom,
    centerX: clamp(Number.isFinite(requestedX) ? requestedX : 0.5, edgeX, 1 - edgeX),
    centerY: clamp(Number.isFinite(requestedY) ? requestedY : 0.5, edgeY, 1 - edgeY),
  };
};

export const getGridWorldPointFromViewport = (clientX, clientY, rect, view, aspectRatio, fitMode = 'contain') => {
  if (!rect?.width || !rect?.height) return null;
  const camera = normalizeGridViewState(view, {
    width: rect.width,
    height: rect.height,
    aspectRatio,
    fitMode,
  });
  const content = fitGridContentToViewport(rect.width, rect.height, aspectRatio, fitMode);
  if (!content.width || !content.height) return null;
  return {
    x: camera.centerX + (clientX - rect.left - rect.width / 2) / (content.width * camera.zoom),
    y: camera.centerY + (clientY - rect.top - rect.height / 2) / (content.height * camera.zoom),
  };
};

export const getGridViewStorageKey = (sceneId, role = 'gm') => {
  if (!sceneId) return null;
  return `${STORAGE_PREFIX}:${encodeURIComponent(role)}:${encodeURIComponent(sceneId)}`;
};

export const loadGridViewState = (sceneId, role = 'gm') => {
  const key = getGridViewStorageKey(sceneId, role);
  if (!key || typeof window === 'undefined') return { ...DEFAULT_GRID_VIEW_STATE };

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? normalizeGridViewState(JSON.parse(raw)) : { ...DEFAULT_GRID_VIEW_STATE };
  } catch {
    return { ...DEFAULT_GRID_VIEW_STATE };
  }
};

export const saveGridViewState = (sceneId, view, role = 'gm') => {
  const normalized = normalizeGridViewState(view);
  const key = getGridViewStorageKey(sceneId, role);
  if (!key || typeof window === 'undefined') return normalized;

  try {
    window.localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    // Keep view interaction available if storage is unavailable.
  }
  return normalized;
};